import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog, DialogContent, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import CustomButton from 'components/CustomButton';
import CustomDropdown from 'components/CustomDropdown';
import AddressInformationSection from 'containers/NewCustomerPage/AddressInformationSection';
import { getPosUseTypes, NEW_CUSTOMER_DEFAULT_VALUES } from 'containers/NewCustomerPage/constants';
import OthersSection from 'containers/NewCustomerPage/OthersSections';
import PersonalInformationSection from 'containers/NewCustomerPage/PersonalInformationSection';

import { getUsStates } from 'services/Client';
import { addCustomerToQueue, createPOSCustomer } from 'services/POS';

import { ProductUseType } from 'constants/enums';
import { Option } from 'types/option.interface';
import { handleApiErrors } from 'utils/errorUtils';
import { newCustomerSchema, NewCustomerFormData } from 'validation/newCustomerSchema';

import {
  CloseDialogButton, CloseModalIcon, DialogHeader, DialogHeaderText,
} from './index.styled';

interface NewCustomerModalProps {
  onClose: () => void;
}

const NewCustomerModal = ({ onClose }: NewCustomerModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingAddCustomerToQueue, setIsLoadingAddCustomerToQueue] = useState(false);
  const [states, setStates] = useState<Option[]>([]);

  const {
    handleSubmit,
    control,
    reset,
    resetField,
    watch,
  } = useForm<NewCustomerFormData>({
    defaultValues: NEW_CUSTOMER_DEFAULT_VALUES,
    resolver: yupResolver(newCustomerSchema),
  });

  const getStates = async () => {
    try {
      setIsLoadingStates(true);
      const { data } = await getUsStates();
      setStates(data.list);
    } catch (err) {
      handleApiErrors(err);
    } finally {
      setIsLoadingStates(false);
    }
  };

  const createCustomer = async (customer: NewCustomerFormData) => {
    try {
      setIsLoading(true);
      const { data } = await createPOSCustomer(customer);
      toast.success('Customer created successfully');
      await addCustomerToQueueHandler(data.id);
      onClose();
    } catch (err) {
      handleApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  const addCustomerToQueueHandler = async (customerId: string) => {
    if (!customerId) {
      toast.error('Customer not found');
      return;
    }

    try {
      setIsLoadingAddCustomerToQueue(true);
      await addCustomerToQueue(customerId);
      toast.success('Customer added to queue successfully');
      reset();
    } catch (err) {
      handleApiErrors(err);
    } finally {
      setIsLoadingAddCustomerToQueue(false);
    }
  };

  const useTypeField = watch('type');

  useEffect(() => {
    resetField('patientNumber');
    resetField('expirationDate');
  }, [useTypeField]);

  useEffect(() => {
    getStates();

    return () => {
      reset();
    };
  }, []);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogHeader>
        <DialogHeaderText>New Customer</DialogHeaderText>
        <CloseDialogButton
          id="button-closeDialogHeader"
          variant="outlined"
          onClick={onClose}
        >
          <CloseModalIcon />
        </CloseDialogButton>
      </DialogHeader>

      <DialogContent>
        <form onSubmit={handleSubmit(createCustomer)}>
          <Box display="flex" gap={1} flexDirection="column">
            <Box mb={1}>
              <Controller
                control={control}
                name="type"
                render={({ field, fieldState }) => (
                  <CustomDropdown
                    {...field}
                    fullWidth
                    options={getPosUseTypes()}
                    label="Customer Type"
                    required
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>

            <PersonalInformationSection control={control} />
            <AddressInformationSection control={control} states={states} />
            <OthersSection
              control={control}
              states={states}
              isPatientNumberRequired={useTypeField === ProductUseType.is_medical_use}
            />

            <CustomButton
              id="button-submitCustomer"
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              loading={isLoading || isLoadingStates || isLoadingAddCustomerToQueue}
            >
              Create & Add to Queue
            </CustomButton>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewCustomerModal;
